import { useSkillsAndRoles } from '@/hooks/useSkillsAndRoles';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { ProjectBrief } from '@/api/adapter/projectBrief';
import type { ProjectParticipant } from '@/api/adapter/projectParticipants';
import type { MilestonePresentation } from '@/api/adapter/proposalReviews';
import { profileImageUrl } from '@/hooks/useProfile';
import {
  milestoneTotal,
  plannedMilestoneBudget,
  weeksIn,
  type PlanningMilestone,
} from './planning-draft';
const budgetLabels = {
  Below10000: 'Below $10,000',
  From10000To50000: '$10,000–$50,000',
  From50000To100000: '$50,000–$100,000',
  Above100000: 'Above $100,000',
};
export function ReviewPanelCard({
  definitions,
  coordinatorPercent,
  details,
  brief,
  coordinator,
  comment,
  onComment,
}: {
  definitions: PlanningMilestone[];
  coordinatorPercent: number | undefined;
  details: MilestonePresentation[];
  brief?: ProjectBrief;
  coordinator?: ProjectParticipant;
  comment: string;
  onComment: (comment: string) => void;
}) {
  const { currency } = useDaoConfig();
  const consultantAvatarUrl =
    coordinator?.workerImage && coordinator.profiles
      ? profileImageUrl(coordinator.profiles.principalId, 'worker')
      : undefined;
  const amount = (value: string) =>
    /^(0|[1-9][0-9]*)$/.test(value) ? BigInt(value) : 0n;
  const allocated = definitions.map((m) => {
    try {
      return plannedMilestoneBudget(m, coordinatorPercent);
    } catch {
      return null;
    }
  });
  const ready = coordinatorPercent !== undefined && allocated.every(Boolean);
  const coordinatorBudget = allocated
    .reduce((sum, m) => sum + amount(m?.coordinatorFee ?? '0'), 0n)
    .toString();
  const totalBudget = definitions
    .reduce((sum, m) => sum + BigInt(milestoneTotal(m)), 0n)
    .toString();
  const workerBudget = (
    BigInt(totalBudget) - BigInt(coordinatorBudget)
  ).toString();
  const { getRoleName } = useSkillsAndRoles();
  const teamProfiles = definitions.flatMap((m) =>
    m.requirements.map((r) => ({
      role: getRoleName(r.roleId) ?? 'Select Role',
      proficiency:
        details
          .find((d) => d.key === m.key)
          ?.requirements.find((d) => d.key === r.key)?.experience ??
        'Not specified',
      availability:
        (r.minutes / 60 / Math.max(1, weeksIn(m.window))).toLocaleString(
          undefined,
          { maximumFractionDigits: 2 }
        ) + ' hrs/week',
      count: 1,
    }))
  );
  return (
    <div
      className={`flex flex-col gap-[--spacing-14] rounded-[--radi-5] border border-[--state-brand-active] bg-[--base-surface-2] p-[--spacing-10] transition-all duration-200`}
    >
      {/* Header section (Frame 129) */}
      <div className="flex flex-col gap-[--spacing-9] text-left">
        <div className="flex items-center gap-[--spacing-7]">
          <div className="flex h-[40px] w-[40px] items-center justify-center overflow-hidden rounded-full border border-[--base-border] bg-[--base-fill-1]">
            {consultantAvatarUrl ? (
              <img
                src={consultantAvatarUrl}
                alt="avatar"
                className="h-full w-full object-cover"
              />
            ) : (
              <i className="ri-user-smile-line text-xl text-[--base-fill-5]" />
            )}
          </div>
          <h2 className="text-xl font-normal">Your Review</h2>
        </div>

        <p className="text-sm font-normal text-[--text-dark-secondary]">
          Provide an explanation of your proposal and, if you did any, of the
          changes made to the project scope.
        </p>
      </div>

      <>
        <div className="flex flex-col gap-[--spacing-8] text-left">
          <span className="text-sm font-bold text-[--text-dark-tertiary]">
            Team
          </span>
          <div className="flex flex-col gap-[--spacing-12]">
            {teamProfiles.length > 0 ? (
              teamProfiles.map((tp, index) => (
                <div
                  key={index}
                  className="flex items-center gap-[--spacing-12] pl-[--spacing-8]"
                >
                  {/* Quantity */}
                  <span className="text-center text-lg font-semibold text-[--text-dark-primary]">
                    {tp.count}
                  </span>
                  {/* Role + Level */}
                  <div className="flex flex-1 flex-col">
                    <span className="text-lg font-medium text-[--text-dark-primary]">
                      {tp.role}
                    </span>
                    <span className="text-sm font-medium capitalize text-[--text-dark-tertiary]">
                      {tp.proficiency || 'Not specified'}
                    </span>
                  </div>
                  {/* Availability */}
                  <span className="text-right text-sm font-medium text-[--text-dark-tertiary]">
                    {tp.availability}
                  </span>
                </div>
              ))
            ) : (
              <p className="pl-4 text-xs italic text-[--text-dark-tertiary]">
                No roles selected in milestones.
              </p>
            )}
          </div>
        </div>

        {/* Budget Summary Section (Frame 89) */}
        <div className="flex flex-col gap-[--spacing-5] text-left">
          {/* Target Budget */}
          <div className="flex items-center justify-between gap-[--spacing-7] text-balance text-sm font-medium text-[--text-dark-tertiary]">
            <span className="flex-1">Client’s indicative budget (USD)</span>
            <span>
              {brief
                ? budgetLabels[brief.indicativeBudget.range]
                : 'Not specified'}
            </span>
          </div>

          {/* Inner details container (Frame 174) */}
          <div className="flex flex-col gap-[--spacing-5] border-b border-[--base-border] py-[--spacing-5]">
            {/* Project Budget */}
            <div className="flex items-center justify-between gap-[--spacing-7] text-base font-medium">
              <span className="flex-1 text-[--text-dark-primary]">
                Project Budget
              </span>
              <span className="text-[--text-dark-primary]">
                {ready ? `${workerBudget} ${currency.symbol}` : '—'}
              </span>
            </div>
            {/* Coordinator fees from the milestone definitions. */}
            <div className="flex items-center justify-between gap-[--spacing-7] text-base font-medium">
              <span className="flex-1 text-[--text-dark-primary]">
                Your Payment
              </span>
              <span className="text-sm text-[--text-dark-tertiary]">
                {coordinatorPercent === undefined
                  ? '—'
                  : `${totalBudget} ${currency.symbol} x ${coordinatorPercent}%`}
              </span>
              <span className="text-[--text-dark-primary]">
                {ready ? `${coordinatorBudget} ${currency.symbol}` : '—'}
              </span>
            </div>
          </div>

          {/* Proposed Budget (Frame 91) */}
          <div className="flex items-baseline justify-between gap-[--spacing-7] text-lg font-medium text-[--text-dark-primary]">
            <span>Total execution budget</span>
            <span>{totalBudget} {currency.symbol}</span>
          </div>
        </div>

        {/* Commentary Section */}
        <div className="flex flex-col gap-2 text-left">
          <label className="hidden">Commentary</label>
          <textarea
            aria-label="Your Review commentary"
            value={comment}
            onChange={(e) => onComment(e.target.value)}
            placeholder="Type here your commentary..."
            rows={4}
          />
        </div>
      </>
    </div>
  );
}
